import { useState, useEffect } from 'react';
import { API_V1, authFetch, formatAmount, formatAmountCompact } from '../constants';
import StatusBadge from '../components/ui/StatusBadge';
import PageLayout from '../components/ui/PageLayout';
import KpiGrid from '../components/ui/KpiGrid';
import DataTable, { Column } from '../components/ui/DataTable';
import ActionButton from '../components/ui/ActionButton';
import TabBar from '../components/ui/TabBar';
import DrawdownBar from '../components/ui/DrawdownBar';
import { useParams, useNavigate } from 'react-router-dom';

const API = `${API_V1}/contracts`;

interface Obligation { id: number; obligation_type: string; description: string; due_date: string; amount: number | null; completion_pct: number; status: string; }
interface Link { id: number; linked_type: string; linked_id: number; linked_amount: number | null; }
interface Contract { id: number; contract_number: string; contract_type: string; contract_subtype?: string | null; counterparty_id: string; title: string; description: string; total_value: number; committed_amount?: number | null; consumed_amount?: number | null; currency?: string; lifecycle_status: string; effective_date: string; expiration_date: string; auto_renewal: boolean; parent_contract_id: number | null; version: number; risk_score: number | null; extraction_confidence: number | null; has_extraction: boolean; flagged_count: number; obligations: Obligation[]; links: Link[]; }
interface PriceItem { id: number; item_code: string; description: string; unit_price: number; unit_of_measure?: string | null; min_qty: number | null; max_qty: number | null; valid_from?: string | null; valid_to?: string | null; gl_account?: string | null; currency: string; }
interface BillingEntry { id: number; period: string; amount: number; due_date: string | null; description: string | null; status: string; }
interface ExtractionData { contract_id: number; risk_score: number | null; extraction_confidence: number | null; flagged_clauses: any[]; price_list: PriceItem[]; billing_schedule: BillingEntry[]; }

interface DrawdownSO { id: number; so_number: string; order_date: string | null; total_amount: number; currency: string; lifecycle_status: string; customer_id: string; customer_name: string | null; }
interface DrawdownData {
  contract_id: number;
  contract_number: string;
  contract_subtype: string | null;
  is_msa: boolean;
  committed_amount: number;
  consumed_amount: number;
  remaining_amount: number;
  pct_used: number;
  warning: string | null;
  currency: string;
  sales_orders: DrawdownSO[];
  so_count: number;
}

interface HistoryEntry {
  id: number;
  from_status: string | null;
  to_status: string;
  transitioned_by: number | null;
  transitioned_by_email: string | null;
  transitioned_by_name: string | null;
  transitioned_by_doa_level: string | null;
  contract_value: number | null;
  transitioned_at: string;
}
interface HistoryData {
  contract_id: number;
  history: HistoryEntry[];
}

interface SubscriptionRow {
  contract_id: number;
  contract_number: string;
  title: string | null;
  customer_name: string | null;
  entity_code: string | null;
  currency: string;
  lifecycle_status: string;
  effective_date: string | null;
  expiration_date: string | null;
  term_months: number;
  monthly_amount: number;
  billing_frequency: string;
  total_contract_value: number;
  auto_renew: boolean;
  cancellation_notice_days: number;
}

const thStyle: React.CSSProperties = {
  padding: '10px 14px', fontSize: 11, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.04em',
  color: 'var(--text-hint)', borderBottom: '2px solid var(--border-subtle)',
  background: 'var(--bg-card)', whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)',
  borderBottom: '1px solid var(--border-subtle)',
};

export default function ContractDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [contract, setContract] = useState<Contract | null>(null);
  const [extraction, setExtraction] = useState<ExtractionData | null>(null);
  const [drawdown, setDrawdown] = useState<DrawdownData | null>(null);
  const [history, setHistory] = useState<HistoryData | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('overview');

  useEffect(() => {
    if (id) {
      Promise.all([
        authFetch(`${API}/${id}`).then(r => r.ok ? r.json() : null),
        authFetch(`${API}/extraction/${id}`).then(r => r.ok ? r.json() : null),
        authFetch(`${API}/${id}/drawdown`).then(r => r.ok ? r.json() : null),
        authFetch(`${API}/${id}/history`).then(r => r.ok ? r.json() : null),
        authFetch(`${API_V1}/revenue/subscriptions?entity_code=*`).then(r => r.ok ? r.json() : null),
      ]).then(([c, e, d, h, subs]) => {
        setContract(c);
        setExtraction(e);
        setDrawdown(d);
        setHistory(h);
        if (subs && Array.isArray(subs.subscriptions)) {
          const match = subs.subscriptions.find((s: SubscriptionRow) => s.contract_id === Number(id));
          setSubscription(match || null);
        }
        setLoading(false);
      });
    }
  }, [id]);

  if (loading) return <PageLayout title="Contract Detail"><div style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading...</div></PageLayout>;
  if (!contract) return <PageLayout title="Contract Detail"><div style={{ padding: 40, textAlign: 'center', color: 'var(--negative)' }}>Contract not found.</div></PageLayout>;

  const isMSA = (contract.contract_subtype || '').toUpperCase() === 'MSA_VALUE';
  const isSubscription = (contract.contract_subtype || '').toUpperCase() === 'SUBSCRIPTION';
  const currency = contract.currency || drawdown?.currency || 'USD';

  // Subscription derived display values. Safe to compute even when subscription
  // is null — rendering is gated on isSubscription below.
  const subNextBillingDate = (() => {
    if (!subscription?.effective_date) return null;
    const today = new Date();
    const start = new Date(subscription.effective_date);
    // First billing is day 1 of subscription. Subsequent are day 1 of each
    // following month/quarter/year. Next billing = next period boundary >= today.
    if (today < start) return subscription.effective_date;
    const freqMonths = subscription.billing_frequency === 'ANNUAL' ? 12
                     : subscription.billing_frequency === 'QUARTERLY' ? 3
                     : 1;
    const monthsElapsed = (today.getFullYear() - start.getFullYear()) * 12 + (today.getMonth() - start.getMonth());
    const periodsElapsed = Math.floor(monthsElapsed / freqMonths);
    const nextMonth = (periodsElapsed + 1) * freqMonths;
    const next = new Date(start);
    next.setMonth(next.getMonth() + nextMonth);
    return next.toISOString().slice(0, 10);
  })();

  const subDecisionDeadline = (() => {
    if (!subscription?.expiration_date || !subscription.cancellation_notice_days) return null;
    const end = new Date(subscription.expiration_date);
    end.setDate(end.getDate() - subscription.cancellation_notice_days);
    return end.toISOString().slice(0, 10);
  })();

  const subDaysToRenewal = (() => {
    if (!subscription?.expiration_date) return null;
    const end = new Date(subscription.expiration_date).getTime();
    const now = Date.now();
    return Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
  })();

  const priceColumns: Column[] = [
    { key: 'item_code', label: 'Item Code', render: (v: string) => <span style={{ fontWeight: 600 }}>{v || '-'}</span> },
    { key: 'description', label: 'Description' },
    { key: 'unit_of_measure', label: 'Unit', render: (v: string | null) => v || '-' },
    { key: 'unit_price', label: 'Unit Price', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'valid_from', label: 'Valid From', render: (v: string | null) => v || '-' },
    { key: 'valid_to', label: 'Valid To', render: (v: string | null) => v || '-' },
    { key: 'gl_account', label: 'GL', render: (v: string | null) => v || '-' },
  ];

  const drawdownSoColumns: Column[] = [
    { key: 'so_number', label: 'SO #', render: (v: string, row: DrawdownSO) => (
      <a
        href={`/sales-orders`}
        onClick={e => { e.preventDefault(); navigate('/sales-orders'); }}
        style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}
        title={`id ${row.id}`}
      >
        {v}
      </a>
    )},
    { key: 'order_date', label: 'Order Date', render: (v: string | null) => v || '-' },
    { key: 'customer_name', label: 'Customer', render: (v: string | null, row: DrawdownSO) => v || row.customer_id || '-' },
    { key: 'total_amount', label: 'Amount', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'lifecycle_status', label: 'Status', render: (v: string) => <StatusBadge status={v} /> },
  ];

  const billingColumns: Column[] = [
    { key: 'period', label: 'Period', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'description', label: 'Description', render: (v: string | null) => v || '-' },
    { key: 'due_date', label: 'Due Date', render: (v: string | null) => v || '-' },
    { key: 'amount', label: 'Amount', align: 'right', render: (v: number) => formatAmount(v) },
    { key: 'status', label: 'Status', render: (v: string) => <StatusBadge status={v} /> },
  ];

  const obligationColumns: Column[] = [
    { key: 'obligation_type', label: 'Type', render: (v: string) => <span style={{ fontWeight: 600, fontSize: 10 }}>{v}</span> },
    { key: 'description', label: 'Description', render: (v: string) => v || '-' },
    { key: 'due_date', label: 'Due Date', render: (v: string) => v || '-' },
    { key: 'amount', label: 'Amount', render: (v: number | null) => v != null ? formatAmount(v) : '-' },
    { key: 'completion_pct', label: 'Progress', render: (v: number) => (
      <div style={{ width: 60, height: 6, background: 'var(--border-subtle)', borderRadius: 3 }}>
        <div style={{ width: `${v}%`, height: '100%', background: 'var(--positive)', borderRadius: 3 }} />
      </div>
    )},
    { key: 'status', label: 'Status', render: (v: string) => <StatusBadge status={v} /> },
  ];

  const linkColumns: Column[] = [
    { key: 'linked_type', label: 'Type', render: (v: string) => <span style={{ fontWeight: 600 }}>{v}</span> },
    { key: 'linked_id', label: 'ID', render: (v: number) => <span style={{ color: 'var(--navy-800)' }}>#{v}</span> },
    { key: 'linked_amount', label: 'Amount', render: (v: number | null) => v != null ? formatAmount(v) : '-' },
  ];

  // ── Shared sub-sections (used by Overview tab or flat layout) ────

  const overviewSection = (
    <>
      {/* Key Fields */}
      <KpiGrid columns={4} items={[
        { label: 'Type', value: contract.contract_type },
        { label: 'Counterparty', value: contract.counterparty_id || '-' },
        { label: 'Value', value: formatAmountCompact(contract.total_value) },
        { label: 'Auto-Renewal', value: contract.auto_renewal ? 'YES' : 'NO' },
        { label: 'Effective', value: contract.effective_date || '-' },
        { label: 'Expiration', value: contract.expiration_date || '-' },
        { label: 'Parent', value: contract.parent_contract_id ? `#${contract.parent_contract_id}` : '-' },
        { label: 'Version', value: contract.version },
      ]} />

      {contract.description && (
        <div style={{ padding: 12, background: 'var(--bg-card)', borderRadius: 8, marginBottom: 20, fontSize: 13, lineHeight: 1.5, border: '1px solid var(--border-subtle)' }}>
          {contract.description}
        </div>
      )}

      {/* AI Extraction badges */}
      {contract.has_extraction && (
        <KpiGrid columns={4} items={[
          { label: 'Risk Score', value: contract.risk_score ?? '-', color: (contract.risk_score ?? 0) >= 60 ? 'var(--negative)' : (contract.risk_score ?? 0) >= 30 ? 'var(--warning)' : 'var(--positive)' },
          { label: 'AI Confidence', value: contract.extraction_confidence != null ? `${Math.round(contract.extraction_confidence * 100)}%` : '-', color: 'var(--navy-800)' },
          { label: 'Flagged Clauses', value: contract.flagged_count, color: contract.flagged_count > 0 ? 'var(--negative)' : 'var(--positive)' },
          { label: 'Source', value: 'AI-Extracted', color: 'var(--navy-800)' },
        ]} />
      )}

      {/* Flagged clauses */}
      {extraction?.flagged_clauses && extraction.flagged_clauses.length > 0 && (
        <div style={{ marginBottom: 20, border: '1px solid var(--negative)', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ padding: '8px 14px', background: 'rgba(239,68,68,0.06)', fontWeight: 700, fontSize: 11, color: 'var(--negative)', textTransform: 'uppercase' as const }}>
            Flagged Clauses ({extraction.flagged_clauses.length})
          </div>
          {extraction.flagged_clauses.map((fc: any, i: number) => (
            <div key={i} style={{ padding: '10px 14px', borderTop: i > 0 ? '1px solid var(--border-subtle)' : 'none', background: 'var(--bg-card)' }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: fc.severity === 'HIGH' ? 'var(--negative)' : fc.severity === 'MEDIUM' ? 'var(--warning)' : 'var(--text-secondary)', color: '#fff' }}>{fc.severity}</span>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--navy-900)' }}>{fc.issue}</span>
              </div>
              {fc.clause_text && <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontStyle: 'italic', marginBottom: 2 }}>"{fc.clause_text}"</div>}
              {fc.recommendation && <div style={{ fontSize: 11, color: 'var(--navy-800)' }}>{fc.recommendation}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Billing Schedule */}
      {extraction?.billing_schedule && extraction.billing_schedule.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: 'var(--navy-900)' }}>Billing Schedule ({extraction.billing_schedule.length} entries)</h2>
          <DataTable columns={billingColumns} rows={extraction.billing_schedule} rowKey="id" />
        </div>
      )}

      <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: 'var(--navy-900)' }}>Obligations ({contract.obligations.length})</h2>
      <DataTable
        columns={obligationColumns}
        rows={contract.obligations}
        rowKey="id"
        emptyMessage="No obligations."
      />

      <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, marginTop: 24, color: 'var(--navy-900)' }}>Linked Documents ({contract.links.length})</h2>
      <DataTable
        columns={linkColumns}
        rows={contract.links}
        rowKey="id"
        emptyMessage="No linked documents."
      />
    </>
  );

  const rateCardSection = (
    <div style={{ marginBottom: 20 }}>
      <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: 'var(--navy-900)' }}>
        Rate Card ({(extraction?.price_list || []).length} items)
      </h2>
      {extraction?.price_list && extraction.price_list.length > 0 ? (
        <DataTable columns={priceColumns} rows={extraction.price_list} rowKey="id" />
      ) : (
        <div style={{ padding: 20, background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', textAlign: 'center', color: 'var(--text-hint)', fontSize: 13 }}>
          No rate card items. This MSA has no price list populated yet.
        </div>
      )}
    </div>
  );

  const historySection = (
    <div>
      <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: 'var(--navy-900)' }}>
        State Transitions ({history?.history.length || 0})
      </h2>
      {history?.history && history.history.length > 0 ? (
        <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden', background: 'var(--bg-card)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>When</th>
                <th style={thStyle}>From</th>
                <th style={thStyle}>To</th>
                <th style={thStyle}>By</th>
                <th style={thStyle}>DOA</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Value at decision</th>
              </tr>
            </thead>
            <tbody>
              {history.history.map((h: HistoryEntry) => (
                <tr key={h.id}>
                  <td style={tdStyle}>
                    {h.transitioned_at ? new Date(h.transitioned_at).toLocaleString() : '-'}
                  </td>
                  <td style={tdStyle}>
                    {h.from_status
                      ? <StatusBadge status={h.from_status} />
                      : <span style={{ color: 'var(--text-hint)', fontSize: 11 }}>(initial)</span>}
                  </td>
                  <td style={tdStyle}>
                    <StatusBadge status={h.to_status} />
                  </td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 600, fontSize: 12 }}>{h.transitioned_by_name || h.transitioned_by_email || `user #${h.transitioned_by ?? '?'}`}</div>
                    {h.transitioned_by_email && h.transitioned_by_name && (
                      <div style={{ fontSize: 10, color: 'var(--text-hint)' }}>{h.transitioned_by_email}</div>
                    )}
                  </td>
                  <td style={tdStyle}>
                    {h.transitioned_by_doa_level ? (
                      <span style={{
                        fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4,
                        background: 'rgba(59,130,246,0.12)', color: 'var(--navy-800)',
                        textTransform: 'uppercase' as const, letterSpacing: '0.04em',
                      }}>
                        {h.transitioned_by_doa_level}
                      </span>
                    ) : '-'}
                  </td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {h.contract_value != null ? formatAmount(h.contract_value) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ padding: 20, background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', textAlign: 'center', color: 'var(--text-hint)', fontSize: 13 }}>
          No state transitions recorded. History tracking started after Phase 3c-ext (2026-04-14).
        </div>
      )}
    </div>
  );

  const drawdownSection = (
    <div>
      {drawdown && drawdown.committed_amount > 0 ? (
        <>
          <KpiGrid columns={4} items={[
            { label: 'Committed', value: formatAmountCompact(drawdown.committed_amount), color: 'var(--text-primary)' },
            { label: 'Consumed', value: formatAmountCompact(drawdown.consumed_amount), color: 'var(--accent)' },
            { label: 'Remaining', value: formatAmountCompact(drawdown.remaining_amount), color: 'var(--positive)' },
            { label: 'Utilization', value: `${drawdown.pct_used.toFixed(1)}%`, color: drawdown.warning === 'EXCEEDED' ? 'var(--negative)' : drawdown.warning ? 'var(--warning)' : 'var(--positive)' },
          ]} />

          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: 16, marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Draw-Down Progress
              </div>
              {drawdown.warning && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    padding: '2px 8px',
                    borderRadius: 4,
                    background: drawdown.warning === 'EXCEEDED' ? 'rgba(239,68,68,0.12)' : drawdown.warning === 'NEAR_CEILING' ? 'rgba(251,146,60,0.12)' : 'rgba(245,158,11,0.12)',
                    color: drawdown.warning === 'EXCEEDED' ? 'var(--negative)' : drawdown.warning === 'NEAR_CEILING' ? '#fb923c' : 'var(--warning)',
                    textTransform: 'uppercase' as const,
                    letterSpacing: '0.04em',
                  }}
                >
                  {drawdown.warning.replace(/_/g, ' ')}
                </span>
              )}
            </div>
            <DrawdownBar pctUsed={drawdown.pct_used} warning={drawdown.warning as any} height={14} />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: 11, color: 'var(--text-secondary)' }}>
              <span>$0</span>
              <span>{formatAmount(drawdown.committed_amount)}</span>
            </div>
          </div>

          <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: 'var(--navy-900)' }}>
            Sales Orders ({drawdown.so_count})
          </h2>
          {drawdown.sales_orders.length > 0 ? (
            <DataTable columns={drawdownSoColumns} rows={drawdown.sales_orders} rowKey="id" />
          ) : (
            <div style={{ padding: 20, background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', textAlign: 'center', color: 'var(--text-hint)', fontSize: 13 }}>
              No Sales Orders have drawn from this MSA yet.
            </div>
          )}
        </>
      ) : (
        <div style={{ padding: 20, background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', textAlign: 'center', color: 'var(--text-hint)', fontSize: 13 }}>
          Draw-down tracking is available for MSA contracts. This contract has no committed amount set.
        </div>
      )}
    </div>
  );

  const subscriptionSection = (
    <div>
      {subscription ? (
        <>
          <KpiGrid columns={4} items={[
            { label: 'Monthly Fee', value: formatAmountCompact(subscription.monthly_amount), color: 'var(--text-primary)' },
            { label: 'Annualized (ARR contribution)', value: formatAmountCompact(subscription.monthly_amount * 12), color: 'var(--text-primary)' },
            { label: 'Total Contract Value', value: formatAmountCompact(subscription.total_contract_value), color: 'var(--accent)' },
            { label: 'Term', value: `${subscription.term_months} months`, color: 'var(--text-primary)' },
          ]} />

          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: 16, marginBottom: 20 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 12 }}>
              Subscription Details
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, fontSize: 13 }}>
              <div>
                <div style={{ color: 'var(--text-hint)', fontSize: 11, marginBottom: 2 }}>Billing Frequency</div>
                <div style={{ fontWeight: 600 }}>{subscription.billing_frequency}</div>
              </div>
              <div>
                <div style={{ color: 'var(--text-hint)', fontSize: 11, marginBottom: 2 }}>Next Billing Date</div>
                <div style={{ fontWeight: 600 }}>{subNextBillingDate || '-'}</div>
              </div>
              <div>
                <div style={{ color: 'var(--text-hint)', fontSize: 11, marginBottom: 2 }}>Start Date</div>
                <div style={{ fontWeight: 600 }}>{subscription.effective_date || '-'}</div>
              </div>
              <div>
                <div style={{ color: 'var(--text-hint)', fontSize: 11, marginBottom: 2 }}>End Date (Initial Term)</div>
                <div style={{ fontWeight: 600 }}>{subscription.expiration_date || '-'}</div>
              </div>
              <div>
                <div style={{ color: 'var(--text-hint)', fontSize: 11, marginBottom: 2 }}>Auto-Renewal</div>
                <div style={{ fontWeight: 600 }}>
                  {subscription.auto_renew ? 'Yes — ' + subscription.cancellation_notice_days + ' day notice' : 'No'}
                </div>
              </div>
              <div>
                <div style={{ color: 'var(--text-hint)', fontSize: 11, marginBottom: 2 }}>Renewal Decision Deadline</div>
                <div style={{ fontWeight: 600 }}>
                  {subDecisionDeadline || '-'}
                  {subDaysToRenewal != null && (
                    <span style={{ color: 'var(--text-hint)', fontWeight: 400, marginLeft: 6 }}>
                      ({subDaysToRenewal} days away)
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div style={{ padding: 12, background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.18)', borderRadius: 8, fontSize: 12, color: 'var(--text-secondary)', marginBottom: 20 }}>
            <strong style={{ color: 'var(--navy-800)' }}>ASC 606 series provision:</strong>{' '}
            This subscription is recognized as a single performance obligation over the full {subscription.term_months}-month
            enforceable term per ASC 606-10-25-14(b). Monthly schedule lines measure progress — they are not separate obligations.
            Revenue is recognized ratably (straight-line) regardless of billing frequency. Auto-renewal is excluded from the
            POB boundary per ASC 606-10-32-4 and will be handled as a new contract at renewal.
          </div>
        </>
      ) : (
        <div style={{ padding: 20, background: 'var(--bg-card)', borderRadius: 8, border: '1px solid var(--border-subtle)', textAlign: 'center', color: 'var(--text-hint)', fontSize: 13 }}>
          Subscription metadata not yet available. The AI extraction may still be in progress, or the subscription_fields section was not populated.
        </div>
      )}
    </div>
  );

  return (
    <PageLayout
      title={contract.contract_number}
      subtitle={contract.title || contract.contract_type}
      actions={[
        { label: 'Back to Contracts', onClick: () => navigate('/contracts'), variant: 'secondary' },
      ]}
    >
      {/* Status badge + subtype badge */}
      <div style={{ marginTop: -8, marginBottom: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
        <StatusBadge status={contract.lifecycle_status} />
        {isMSA && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: 4,
              background: 'rgba(139, 92, 246, 0.12)',
              color: 'rgb(139, 92, 246)',
              textTransform: 'uppercase' as const,
              letterSpacing: '0.04em',
            }}
          >
            MSA • {currency} {formatAmount(contract.committed_amount || 0)}
          </span>
        )}
        {isSubscription && (
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              padding: '3px 8px',
              borderRadius: 4,
              background: 'rgba(16, 185, 129, 0.12)',
              color: 'rgb(16, 185, 129)',
              textTransform: 'uppercase' as const,
              letterSpacing: '0.04em',
            }}
          >
            SUBSCRIPTION{subscription ? ` • ${currency} ${formatAmount(subscription.monthly_amount)}/mo` : ''}
          </span>
        )}
      </div>

      {isSubscription ? (
        <>
          <TabBar
            tabs={[
              { key: 'overview', label: 'Overview' },
              { key: 'subscription', label: 'Subscription' },
              { key: 'history', label: 'History', count: history?.history.length || 0 },
            ]}
            active={activeTab}
            onChange={setActiveTab}
          />
          {activeTab === 'overview' && overviewSection}
          {activeTab === 'subscription' && subscriptionSection}
          {activeTab === 'history' && historySection}
        </>
      ) : isMSA ? (
        <>
          <TabBar
            tabs={[
              { key: 'overview', label: 'Overview' },
              { key: 'rate_card', label: 'Rate Card', count: (extraction?.price_list || []).length },
              { key: 'drawdown', label: 'Draw-Down', count: drawdown?.so_count || 0 },
              { key: 'history', label: 'History', count: history?.history.length || 0 },
            ]}
            active={activeTab}
            onChange={setActiveTab}
          />
          {activeTab === 'overview' && overviewSection}
          {activeTab === 'rate_card' && rateCardSection}
          {activeTab === 'drawdown' && drawdownSection}
          {activeTab === 'history' && historySection}
        </>
      ) : (
        <>
          <TabBar
            tabs={[
              { key: 'overview', label: 'Overview' },
              { key: 'history', label: 'History', count: history?.history.length || 0 },
            ]}
            active={activeTab}
            onChange={setActiveTab}
          />
          {activeTab === 'overview' && (
            <>
              {overviewSection}
              {extraction?.price_list && extraction.price_list.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, color: 'var(--navy-900)' }}>
                    Price List ({extraction.price_list.length} items)
                  </h2>
                  <DataTable columns={priceColumns} rows={extraction.price_list} rowKey="id" />
                </div>
              )}
            </>
          )}
          {activeTab === 'history' && historySection}
        </>
      )}
    </PageLayout>
  );
}
